import { DOCUMENT } from '@angular/common';
import {
  Component,
  ContentChild,
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  animationFrameScheduler,
  first,
  observeOn,
  ReplaySubject,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
} from 'rxjs';
import { RxUtils } from '../../utils/rx.utils';
import { OverlayContainerController } from './overlay-container.controller';

@Directive({
  selector: 'ng-template[overlayContent]',
})
export class ContentTemplate {
  constructor(public templateRef: TemplateRef<any>) {}
}

const containerHolderMap = new WeakMap<HTMLElement, number>();

@Component({
  selector: 'app-overlay',
  templateUrl: 'overlay.component.html',
  styleUrls: ['overlay.component.scss'],
})
export class OverlayComponent implements OnInit, OnChanges, OnDestroy {
  @ContentChild(ContentTemplate, { static: false })
  contentTpl: ContentTemplate;

  @ViewChild('overlayEl', { static: false })
  overlayElRef: ElementRef<HTMLDivElement>;

  private get _overlayEl(): HTMLDivElement {
    return this.overlayElRef?.nativeElement;
  }

  @Input()
  visible: boolean;
  @Output()
  visibleChange = new EventEmitter<boolean>();

  @Input()
  overlayMask = true;
  @Input()
  overlayMaskColor = 'rgba(0, 0, 0, 0.64)';
  @Input()
  animate = true;

  @Input()
  overlayClass: string;

  @Output()
  onShowing = new EventEmitter<any>();
  @Output()
  onShown = new EventEmitter<any>();
  @Output()
  onHiding = new EventEmitter<any>();
  @Output()
  onHidden = new EventEmitter<any>();

  @Output()
  onContentReady = new EventEmitter<void>();

  private _visible = false;

  showing = false;
  hiding = false;
  hidden = true;

  rendered = false;

  private _containerTransferRequested = false;
  private _containerTransfered = false;

  @Input()
  deferRendering: boolean = true;
  @Input()
  container: HTMLElement;

  zIndex: number;

  private transferToContainerSubscription: Subscription;
  private stateShownClassApplySubscription: Subscription;

  private overlayElRenderedSubject = new ReplaySubject<void>(1);

  private lifecycle = new Subject<void>();

  constructor(
    private overlayContainerCtrl: OverlayContainerController,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document,
    private zone: NgZone
  ) {}

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.overlayElRenderedSubject
        .pipe(
          switchMap(() => {
            const el = this._overlayEl;

            return RxUtils.observeElementCssTransition(el, {
              onStart: () => {
                const hasStateShownClass = el.classList.contains('state-shown');
                if (!this.showing && hasStateShownClass) {
                  this.zone.runGuarded(() => {
                    this.hidden = false;
                    this.showing = true;
                    if (!this.visible) {
                      this.visible = true;
                      this.visibleChange.emit(this.visible);
                    }
                    this.onShowing.emit();
                  });
                } else if (!this.hiding && !hasStateShownClass) {
                  this.zone.runGuarded(() => {
                    this.hiding = true;
                    this.onHiding.emit();
                  });
                }
              },
              onEnd: () => {
                if (this.showing) {
                  this.zone.runGuarded(() => {
                    this.showing = false;
                    this._visible = true;
                    this.hidden = false;
                    this.onShown.emit();
                  });
                } else if (this.hiding) {
                  this.zone.runGuarded(() => {
                    this._visible = false;
                    this.hiding = false;
                    this.hidden = true;
                    if (this.visible) {
                      this.visible = false;
                      this.visibleChange.emit(this.visible);
                    }
                    this.onHidden.emit();
                  });
                }
              },
              onCancel: () => {
                this.zone.runGuarded(() => {
                  this.showing = false;
                  this.hiding = false;
                });
              },
            });
          }),
          takeUntil(this.lifecycle)
        )
        .subscribe();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.deferRendering || changes.visible) {
      if (!this.rendered && (!this.deferRendering || this.visible)) {
        this.rendered = true;
      }
    }

    if (changes.container && !changes.container.firstChange) {
      if (this.rendered) {
        this.removeElementFromContainer(
          changes.container.previousValue ?? this._document.body
        );

        this.transferContentToContainerIfNeeded(true);
      }
    }

    if (changes.visible) {
      if (this.rendered) {
        this.transferContentToContainerIfNeeded();
      }

      this.toggleStateShownClass();
    }

    if (changes.container || changes.visible) {
      this.bringToFront();
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();

    this.transferToContainerSubscription?.unsubscribe();
    this.stateShownClassApplySubscription?.unsubscribe();

    this.removeElementFromContainer(this.container ?? this._document.body);
  }

  onRendered() {
    this.zone.runOutsideAngular(() => {
      this.overlayElRenderedSubject.next();
    });

    this.zone.runGuarded(() => {
      this.onContentReady.emit();
    });
  }

  bringToFront() {
    const container = this.container ?? this._document.body;

    let zIndex: number;
    if (containerHolderMap.has(container)) {
      zIndex = containerHolderMap.get(container);
      if (zIndex !== this.zIndex) {
        this.zIndex = ++zIndex;
      }
    } else {
      this.zIndex = zIndex = 101;
    }

    containerHolderMap.set(container, zIndex);
  }

  private transferContentToContainerIfNeeded(reset = false) {
    if (reset) {
      this._containerTransferRequested = false;
      this._containerTransfered = false;
    } else if (this._containerTransferRequested) {
      return;
    }

    this._containerTransferRequested = true;
    this.transferToContainerSubscription?.unsubscribe();
    this.transferToContainerSubscription =
      this.overlayElRenderedSubject.subscribe(() => {
        let container = this.container ?? this._document.body;

        this.overlayContainerCtrl.appendElementToContainer(
          container,
          this._overlayEl
        );

        this._containerTransfered = true;
      });
  }

  private removeElementFromContainer(container: HTMLElement) {
    if (this._containerTransfered && this._overlayEl) {
      this.overlayContainerCtrl.removeElementFromContainer(
        container,
        this._overlayEl
      );
    }
  }

  private toggleStateShownClass() {
    this.zone.runOutsideAngular(() => {
      this.stateShownClassApplySubscription?.unsubscribe();
      this.stateShownClassApplySubscription = this.overlayElRenderedSubject
        .pipe(first(), observeOn(animationFrameScheduler))
        .subscribe(() => {
          const el = this._overlayEl;
          if (!el) {
            return;
          }

          if (this.visible) {
            this.renderer.addClass(el, 'state-shown');
          } else {
            this.renderer.removeClass(el, 'state-shown');
          }
        });
    });
  }
}
